import {
  customFunctionImportAlias,
  serializeCustomFunctionsAndLibs,
} from "@/wab/shared/codegen/react-p/custom-functions";
import { getDataSourcesPackageName } from "@/wab/shared/codegen/react-p/data-sources";
import {
  generateDataTokenImports,
  makeComponentDataTokenImports,
} from "@/wab/shared/codegen/react-p/data-tokens/imports";
import {
  getMetadataTypeDefinition,
  serializeGenerateDynamicMetadataFunction,
} from "@/wab/shared/codegen/react-p/page-metadata";
import { serializeGeneratePageMetadataBody } from "@/wab/shared/codegen/react-p/page-metadata/serializer";
import {
  makeDefaultExternalPropsName,
  makePlasmicComponentName,
  makeServerPageSkeletonPropsName,
  makeTaggedPlasmicImport,
  pagePathConflictsWithAppRouter,
  pageReferencesSearchParams,
} from "@/wab/shared/codegen/react-p/serialize-utils";
import { collectComponentServerQueries } from "@/wab/shared/codegen/react-p/server-queries/collect";
import {
  serializeRootComponentQueries,
  serializeServerQueryTree,
} from "@/wab/shared/codegen/react-p/server-queries/serialize-tree";
import {
  makeComponentTypeImport,
  makeDataSourcesQueryTypeImports,
  makeDataSourcesServerQueryImports,
  makeLoaderServerFunctionFileName,
  makePlasmicClientRscComponentFileName,
  makePlasmicClientRscComponentName,
  makePlasmicServerRscComponentFileName,
  makePlasmicServerRscComponentName,
  makeServerQueryTreeTypeImport,
  serializeMakeAppRouterPageCtx,
} from "@/wab/shared/codegen/react-p/server-queries/serializer";
import { isServerQueryWithOperation } from "@/wab/shared/codegen/react-p/server-queries/utils";
import { SerializerBaseContext } from "@/wab/shared/codegen/react-p/types";
import { ComponentExportOutput, ExportOpts } from "@/wab/shared/codegen/types";
import { assert } from "@/wab/shared/common";
import { isPageComponent } from "@/wab/shared/core/components";
import { customFunctionId } from "@/wab/shared/core/query-ids";
import { isHostLessPackage } from "@/wab/shared/core/sites";
import { flattenExprs } from "@/wab/shared/core/tpls";
import {
  extractDataTokenIdentifiers,
  isDataTokenExpr,
} from "@/wab/shared/eval/expression-parser";
import {
  ComponentServerQuery,
  Expr,
  isKnownCustomCode,
  isKnownCustomFunctionExpr,
} from "@/wab/shared/model/classes";

export function getRscMetadata(
  ctx: SerializerBaseContext
): ComponentExportOutput["rscMetadata"] {
  if (!isPageComponent(ctx.component) || !ctx.useRSC) {
    return undefined;
  }

  const serverQueriesExecFunc = serializeServerPageQueriesLoader(ctx);

  return {
    pageWrappers: {
      server: {
        module: serializeServerQueriesServerWrapper(ctx, ctx.exportOpts),
        fileName: makePlasmicServerRscComponentFileName(ctx.component),
      },
      client: {
        module: serializeServerQueriesClientWrapper(ctx, ctx.exportOpts),
        fileName: makePlasmicClientRscComponentFileName(ctx.component),
      },
    },
    serverQueriesExecFunc,
  };
}

export function serializeServerComponentBody(ctx: SerializerBaseContext) {
  return `const { params, searchParams, ...rest } = props;
  const ctx = await makeAppRouterPageCtx({ params, searchParams });
  const { cache: prefetchedCache } = await executePlasmicQueries(
    serverQueryTree,
    { $props: rest, $ctx: ctx }
  );
`;
}

function serializeServerQueriesServerWrapper(
  ctx: SerializerBaseContext,
  opts: ExportOpts
) {
  const { component } = ctx;

  assert(isPageComponent(component), "Expected page component");

  const componentName = makePlasmicServerRscComponentName(component);
  const componentPropsName = `${componentName}Props`;
  const clientComponentName = makePlasmicClientRscComponentName(component);
  const genPropsName = makeDefaultExternalPropsName(component);
  const skeletonPropsName = makeServerPageSkeletonPropsName(component);

  const componentBody = !ctx.hasServerQueries
    ? ""
    : serializeServerComponentBody(ctx);
  const usesSearchParams = pageReferencesSearchParams(component);

  return `
/* eslint-disable */
/* tslint:disable */
// @ts-nocheck
/* prettier-ignore-start */

// This class is auto-generated by Plasmic; please do not edit!
// Plasmic Project: ${ctx.projectConfig.projectId}
// Component: ${component.uuid}
// plasmic-unformatted

import * as React from "react";
${makeComponentTypeImport(opts, component)}

${makeTaggedPlasmicImport(
  clientComponentName,
  makePlasmicClientRscComponentFileName(component),
  component.uuid,
  "rscClient"
)}

${serializeServerPageQueries(ctx)}

${serializeMakeAppRouterPageCtx(ctx, skeletonPropsName, { usesSearchParams })}

export type ${componentPropsName} = ${genPropsName} & ${skeletonPropsName};

export ${
    ctx.hasServerQueries ? "async " : ""
  }function ${componentName}(props: ${componentPropsName}) {
  ${componentBody}

  return (${
    ctx.hasServerQueries
      ? `
    <PlasmicQueryDataProvider prefetchedCache={prefetchedCache}>
      <${clientComponentName} {...rest} />
    </PlasmicQueryDataProvider>`
      : `<${clientComponentName} {...props} />`
  }
  )
}
  `;
}

function serializeServerQueriesClientWrapper(
  ctx: SerializerBaseContext,
  opts: ExportOpts
) {
  const { component } = ctx;

  const componentName = makePlasmicClientRscComponentName(component);
  const plasmicComponentName = makePlasmicComponentName(component);
  const defaultPropsName = makeDefaultExternalPropsName(component);

  return `
"use client";

import * as React from "react";
${makeComponentTypeImport(opts, component, { includePlasmicComponent: true })}

export function ${componentName}(props: ${defaultPropsName}) {
  return (
    <${plasmicComponentName} {...props} />
  )
}
`;
}

/**
 * Extract data token identifiers from server query expressions
 */
export function getDataTokensFromServerQueries(
  queries: ComponentServerQuery[]
): Set<string> {
  const tokenIdentifiers = queries
    .filter(isServerQueryWithOperation)
    .flatMap((query): Expr[] =>
      isKnownCustomFunctionExpr(query.op) ? query.op.args : [query.op]
    )
    .flatMap(flattenExprs)
    .filter(isDataTokenExpr)
    .flatMap(extractDataTokenIdentifiers);
  return new Set(tokenIdentifiers);
}

/**
 * Serializes serverQueryTree for codegen. Uses $$ and data tokens from module scope.
 */
function serializeComponentServerQueryTree(
  ctx: SerializerBaseContext,
  serializedTree: string
) {
  if (!ctx.hasServerQueries) {
    return "";
  }
  const isPage = isPageComponent(ctx.component);
  return `${
    isPage ? "export " : ""
  }const serverQueryTree: QueryComponentNode = ${serializedTree};`;
}

/**
 * Serializes serverQueryTree for client components. Generates a root tree (no children).
 * Used by usePlasmicQueries(qs, { $ctx, $props, $state }) in the render component.
 */
export function serializeRootServerQueryTree(ctx: SerializerBaseContext) {
  if (!ctx.hasServerQueries) {
    return "";
  }
  const serverQueries = ctx.component.serverQueries.filter(
    isServerQueryWithOperation
  );

  const serializedTree = serializeRootComponentQueries(serverQueries, ctx);
  return serializeComponentServerQueryTree(ctx, serializedTree);
}

/**
 * Serialize pageQueryTree for RSC client page components. Only includes page queries to
 * avoid bundling child queries in the client bundle.
 */
export function serializePageQueryTree(ctx: SerializerBaseContext) {
  if (!ctx.hasServerQueries) {
    return "";
  }
  const serverQueries = ctx.component.serverQueries.filter(
    isServerQueryWithOperation
  );
  const serializedTree = serializeRootComponentQueries(serverQueries, ctx);
  return `const pageQueryTree: QueryComponentNode = ${serializedTree};`;
}

function serializeServerPageQueries(ctx: SerializerBaseContext) {
  const { component } = ctx;
  const { customFunctionsAndLibsImport, serializedCustomFunctionsAndLibs } =
    serializeCustomFunctionsAndLibs(ctx);

  const dataTokenImports = makeComponentDataTokenImports(
    component,
    ctx.site,
    ctx.projectConfig.projectId,
    ctx.exportOpts
  );

  if (!ctx.hasServerQueries) {
    // No queries - just emit custom functions, data tokens, and metadata function
    return `
${customFunctionsAndLibsImport}

${serializedCustomFunctionsAndLibs}

${dataTokenImports}

${serializeGenerateDynamicMetadataFunction(ctx)}
`;
  }

  const serverQueryImports = ctx.hasServerQueries
    ? `
${makeDataSourcesServerQueryImports()}
${makeDataSourcesQueryTypeImports()}
${makeServerQueryTreeTypeImport()}
import { PlasmicQueryDataProvider } from "@plasmicapp/react-web/lib/query";`
    : "";
  const tree = collectComponentServerQueries(ctx);
  const serializedTree = serializeServerQueryTree(tree, ctx);

  const module = `
${customFunctionsAndLibsImport}

${serializedCustomFunctionsAndLibs}

${dataTokenImports}
${serverQueryImports}

${serializeGenerateDynamicMetadataFunction(ctx)}
${serializeComponentServerQueryTree(ctx, serializedTree)}
`;
  return module;
}

/**
 * Build the generateMetadata section to append to the loader file.
 * Returns empty string if the component has no page metadata.
 */
function serializeLoaderGenerateMetadataSection(
  ctx: SerializerBaseContext
): string {
  const { component, hasServerQueries } = ctx;

  if (!isPageComponent(component) || !component.pageMeta) {
    return "";
  }
  const propTypeName = "GenerateMetadataProps";
  const usesSearchParams = pageReferencesSearchParams(component);

  const metadataQueryTreeDecl = hasServerQueries
    ? `\nconst metadataQueryTree = { ...serverQueryTree, children: [] };\n`
    : "";

  return `
${getMetadataTypeDefinition()}

${serializeMakeAppRouterPageCtx(ctx, propTypeName, { usesSearchParams })}
${metadataQueryTreeDecl}
export async function generateMetadata(props: ${propTypeName}): Promise<PlasmicPageMetadata> {
  const { params, searchParams } = props;
  ${serializeGeneratePageMetadataBody({ hasServerQueries })}
  return metadata;
}
`;
}

/**
 * Serialize a function to execute page queries for the loader.
 * Also includes generateMetadata if the component has page metadata,
 * since both functions share the same imports and helpers.
 */
function serializeServerPageQueriesLoader(ctx: SerializerBaseContext) {
  let getPlasmicQueriesDataBody: string;

  if (!ctx.hasServerQueries) {
    getPlasmicQueriesDataBody = `return {};`;
  } else {
    getPlasmicQueriesDataBody = `const { cache: prefetchedCache } = await executePlasmicQueries(
    serverQueryTree,
    { $props: props ?? {}, $ctx: ctx }
  );
  return prefetchedCache;`;
  }

  const module = `${serializeServerPageQueries(ctx)}
export async function getPlasmicQueriesData(ctx: any, props?: any) {
  ${getPlasmicQueriesDataBody}
}
${serializeLoaderGenerateMetadataSection(ctx)}
${serializeRequiredQueryFunctions(ctx)}`;
  return {
    module,
    fileName: makeLoaderServerFunctionFileName(ctx.component),
  };
}

export function getPageRouterSkeletonImports(
  ctx: SerializerBaseContext,
  isDynamicRoute: boolean
) {
  let imports = `import { useRouter } from "next/router";
import { PlasmicQueryDataProvider } from "@plasmicapp/react-web/lib/query";`;
  const needsGetStaticProps =
    (ctx.hasServerQueries || ctx.usesComponentLevelQueries) &&
    !pagePathConflictsWithAppRouter(ctx.component.pageMeta?.path);
  if (needsGetStaticProps) {
    const nextTypes = isDynamicRoute
      ? "GetStaticPaths, GetStaticProps"
      : "GetStaticProps";
    imports += `
import type { ${nextTypes} } from "next";
import { extractPlasmicQueryData } from "@plasmicapp/react-web/lib/prepass";`;
  }
  return imports;
}

export function getAppRouterSkeletonImports({
  hasServerQueries,
}: Pick<SerializerBaseContext, "hasServerQueries">) {
  return `import type { Metadata, ResolvingMetadata } from "next";${
    hasServerQueries
      ? `\nimport { executePlasmicQueries } from "${getDataSourcesPackageName()}";`
      : ""
  }`;
}

export function getTanStackSkeletonImports({
  hasServerQueries,
}: Pick<SerializerBaseContext, "hasServerQueries">) {
  return `import { createFileRoute } from "@tanstack/react-router";${
    hasServerQueries
      ? `
import { executePlasmicQueries } from "${getDataSourcesPackageName()}";
import { PlasmicQueryDataProvider } from "@plasmicapp/react-web/lib/query";`
      : ""
  }`;
}

/**
 * Serializes `requiredServerQueryFunctions` export for loader. Includes user-registered
 * custom functions (non-hostless) referenced by queries in this component.
 *
 * loader-react reads this in getPlasmicQueriesData / getPlasmicMetadata
 * to check that every function is present in REGISTERED_CUSTOM_FUNCTIONS before execution,
 * and throws a clear error if not.
 */
function serializeRequiredQueryFunctions(ctx: SerializerBaseContext): string {
  const { component, exportOpts, siteCtx } = ctx;
  if (!exportOpts.useCustomFunctionsStub || exportOpts.isLivePreview) {
    return "";
  }
  const queries = component.serverQueries.filter(isServerQueryWithOperation);
  if (queries.length === 0) {
    return "";
  }
  const seen = new Set<string>();
  const userFunctions = queries.flatMap((q) => {
    if (isKnownCustomCode(q.op)) {
      return [];
    }
    const fn = q.op.func;
    const id = customFunctionId(fn);
    if (seen.has(id)) {
      return [];
    }
    const ownerSite = siteCtx.customFunctionToOwnerSite.get(fn);
    if (!ownerSite || isHostLessPackage(ownerSite)) {
      return [];
    }
    seen.add(id);
    return [fn];
  });

  if (userFunctions.length === 0) {
    return "";
  }

  const entries = userFunctions
    .map(
      (fn) =>
        `  { id: ${JSON.stringify(
          customFunctionId(fn)
        )}, name: ${JSON.stringify(fn.importName)}, namespace: ${JSON.stringify(
          fn.namespace || null
        )}, alias: ${JSON.stringify(customFunctionImportAlias(fn))} }`
    )
    .join(",\n");

  return `
export const requiredServerQueryFunctions = [
${entries}
];`;
}

/**
 * Serialize getStaticProps and imports for Next.js Pages Router skeleton.
 * Uses extractPlasmicQueryData to render the component and extract query data.
 */
export function serializePagesRouterGetStaticProps(
  componentName: string,
  pagePath: string
): string {
  const getStaticProps = `
export const getStaticProps: GetStaticProps = async (context) => {
  const queryCache = await extractPlasmicQueryData(
    <PageParamsProvider__
      route={"${pagePath}"}
      params={context.params}
    >
      <${componentName} />
    </PageParamsProvider__>
  );
  return {
    props: { queryCache },
  };
};
`;

  return getStaticProps;
}

/**
 * Serialize getStaticPaths for Next.js Pages Router dynamic routes.
 */
export function serializePagesRouterGetStaticPaths(): string {
  return `
export const getStaticPaths: GetStaticPaths = async () => {
  console.warn("getStaticPaths was called with an empty paths array. Update this with the set of pages you want to generate statically.");
  return {
    paths: [],
    fallback: "blocking",
  };
};
`;
}

/**
 * Serialize generateMetadata for Next.js App Router skeleton
 * Uses executePlasmicQueries to get dynamic data
 */
export function serializeAppRouterGenerateMetadata(ctx: SerializerBaseContext) {
  const { component, hasServerQueries } = ctx;
  const skeletonPropsName = makeServerPageSkeletonPropsName(component);

  // Generate custom functions and bindings for metadata.
  // The query tree is imported from the server component and children are stripped.
  let metadataQuerySetup = "";
  if (hasServerQueries) {
    const { customFunctionsAndLibsImport, serializedCustomFunctionsAndLibs } =
      serializeCustomFunctionsAndLibs(ctx);

    const tokenIdentifiers = getDataTokensFromServerQueries(
      component.serverQueries
    );
    const dataTokenImports = generateDataTokenImports(
      tokenIdentifiers,
      ctx.site,
      ctx.projectConfig.projectId,
      ctx.exportOpts
    );

    metadataQuerySetup = `
${customFunctionsAndLibsImport}

${serializedCustomFunctionsAndLibs}

${dataTokenImports}

const metadataQueryTree = { ...serverQueryTree, children: [] };
`;
  }

  return `
${metadataQuerySetup}

export async function generateMetadata(
  { params, searchParams }: ${skeletonPropsName},
  parent: ResolvingMetadata
): Promise<Metadata> {
  ${serializeGeneratePageMetadataBody({ hasServerQueries })}

  return { ...(await parent), ...metadata } as unknown as Metadata;
}
`;
}

export function serializeAppRouterGenerateStaticParamsSkeleton(): string {
  return `
// Uncomment and populate to statically pre-render this route at build time.
// Each entry should be an object whose keys match the dynamic segments in the route path.
// See https://nextjs.org/docs/app/api-reference/functions/generate-static-params
//
// export async function generateStaticParams() {
//   return [];
// }
`;
}
